import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getSession } from '../../api/authApi';
import { setCsrfToken } from '../../api/csrf';
import type { SessionResponse } from '../../api/apiSchemas';
import { Brand, CenteredCardLayout } from '../../components/layout/CenteredCardLayout';
import { Spinner } from '../../components/ui';
import { browserNavigation } from '../../lib/browserNavigation';
import { parseAuthCompleteParams } from '../../lib/queryParams';
import { queryKeys } from '../../lib/queryKeys';
import { ROUTES } from '../../lib/routes';
import { toSafeTarget } from '../../lib/safeNavigation';
import { track } from '../../lib/logger';

const RETRY_DELAYS_MS = [350, 900];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Resolves the session after a provider callback, tolerating brief edge
 * propagation by retrying a few times when still anonymous (spec §7.3).
 */
async function resolveSession(signal: AbortSignal): Promise<SessionResponse | null> {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const session = await getSession(signal);
      if (session.authenticated) return session;
      if (attempt === RETRY_DELAYS_MS.length) return session; // final: still anonymous
    } catch {
      if (attempt === RETRY_DELAYS_MS.length) return null;
    }
    await delay(RETRY_DELAYS_MS[attempt]!);
    if (signal.aborted) return null;
  }
  return null;
}

/**
 * Waits for the server-side session cookie, then navigates safely (spec §7.3).
 * Never reads code/state/handoff from the URL.
 */
export default function AuthCompletePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search] = useSearchParams();

  useEffect(() => {
    // Runs per mount with its own controller. Under React StrictMode the first
    // mount is aborted by cleanup and the committed mount completes — so we do
    // NOT use a run-once ref guard here (that would leave the aborted run as the
    // only one and get stuck on the loading screen).
    const { provider } = parseAuthCompleteParams(search);
    track('auth_complete_loaded', {
      route: ROUTES.authComplete,
      ...(provider ? { provider } : {}),
    });

    const controller = new AbortController();
    void (async () => {
      const session = await resolveSession(controller.signal);
      if (controller.signal.aborted) return;

      if (!session || !session.authenticated) {
        navigate(`${ROUTES.error}?code=SESSION_NOT_CREATED`, { replace: true });
        return;
      }

      queryClient.setQueryData(queryKeys.session, session);
      setCsrfToken(session.csrfToken);
      track('auth_session_resolved', { route: ROUTES.authComplete });

      if (session.user.status === 'PENDING_ONBOARDING') {
        navigate(ROUTES.onboarding, { replace: true });
        return;
      }

      const target = toSafeTarget(session.nextUrl, ROUTES.account);
      if (target.startsWith('/')) {
        navigate(target, { replace: true });
      } else {
        browserNavigation.replace(target);
      }
    })();

    return () => controller.abort();
  }, [navigate, queryClient, search]);

  return (
    <CenteredCardLayout>
      <div className="stack" style={{ alignItems: 'center', textAlign: 'center' }}>
        <Brand />
        <Spinner label="Đang hoàn tất đăng nhập" />
        <div aria-live="polite">
          <h1>Đang hoàn tất đăng nhập…</h1>
          <p className="muted">Vui lòng không đóng trang này.</p>
        </div>
      </div>
    </CenteredCardLayout>
  );
}
